<template>
  <div 
    :class="[
      'mgi-skeleton-loader',
      'skeleton-container',
      `skeleton-${type}`,
      { 'skeleton-animated': animated }
    ]"
    :style="containerStyle"
    data-testid="skeleton-container"
    role="status"
    aria-live="polite"
    aria-busy="true"
    :aria-label="`Loading ${type} content`"
  >
    <!-- Card Skeleton -->
    <div v-if="type === 'card'" class="skeleton-card">
      <div class="skeleton-item d-flex align-center mb-3">
        <div class="skeleton-avatar me-3"></div>
        <div class="flex-grow-1">
          <div class="skeleton-line mb-1" style="width: 70%"></div>
          <div class="skeleton-line" style="width: 50%"></div>
        </div>
      </div>
      <div class="skeleton-content">
        <div class="skeleton-line mb-2" style="width: 100%"></div>
        <div class="skeleton-line mb-2" style="width: 85%"></div>
        <div class="skeleton-line" style="width: 60%"></div>
      </div>
    </div>

    <!-- Table Skeleton -->
    <div v-else-if="type === 'table'" class="skeleton-table">
      <div class="skeleton-table-header mb-3">
        <div class="skeleton-row d-flex">
          <div v-for="col in columns" :key="`header-${col}`" class="skeleton-cell flex-grow-1 me-2"></div>
        </div>
      </div>
      <div class="skeleton-table-body">
        <div v-for="row in rows" :key="`row-${row}`" class="skeleton-row d-flex mb-2">
          <div v-for="col in columns" :key="`cell-${row}-${col}`" class="skeleton-cell flex-grow-1 me-2"></div>
        </div>
      </div>
    </div>

    <!-- KPI Skeleton -->
    <div v-else-if="type === 'kpi'" class="skeleton-kpi">
      <div class="skeleton-kpi-header mb-2">
        <div class="skeleton-line" style="width: 40%"></div>
      </div>
      <div class="skeleton-number mb-2" style="height: 32px; width: 60%"></div>
      <div class="skeleton-line" style="width: 30%"></div>
    </div>

    <!-- Chart Skeleton -->
    <div v-else-if="type === 'chart'" class="skeleton-chart">
      <div class="skeleton-chart-header mb-3">
        <div class="skeleton-line" style="width: 30%"></div>
      </div>
      <div class="skeleton-chart-content" style="height: 200px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); border-radius: 4px;"></div>
    </div>

    <!-- List Skeleton -->
    <div v-else-if="type === 'list'" class="skeleton-list">
      <div v-for="item in count" :key="`list-item-${item}`" class="skeleton-list-item d-flex align-center mb-2">
        <div class="skeleton-avatar me-3" style="width: 32px; height: 32px;"></div>
        <div class="flex-grow-1">
          <div class="skeleton-line mb-1" style="width: 60%"></div>
          <div class="skeleton-line" style="width: 40%"></div>
        </div>
      </div>
    </div>

    <!-- Default Rectangle (fallback for invalid types) -->
    <div v-else class="skeleton-card">
      <div class="skeleton-item d-flex align-center mb-3">
        <div class="skeleton-avatar me-3"></div>
        <div class="flex-grow-1">
          <div class="skeleton-line mb-1" style="width: 70%"></div>
          <div class="skeleton-line" style="width: 50%"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  type?: 'card' | 'table' | 'kpi' | 'chart' | 'list' | 'rectangle'
  animated?: boolean
  width?: string | number
  height?: string | number
  count?: number
  rows?: number
  columns?: number
  color?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'card',
  animated: true,
  width: '100%',
  height: 'auto',
  count: 3,
  rows: 5,
  columns: 4,
  color: '#e0e0e0'
})

const containerStyle = computed(() => ({
  width: typeof props.width === 'number' ? `${props.width}px` : props.width,
  height: typeof props.height === 'number' ? `${props.height}px` : props.height,
  '--skeleton-color': props.color
}))

const rectangleStyle = computed(() => ({
  width: '100%',
  height: typeof props.height === 'number' ? `${props.height}px` : (props.height === 'auto' ? '60px' : props.height),
  backgroundColor: props.color,
  borderRadius: '4px'
}))
</script>

<style scoped>
.mgi-skeleton-loader {
  display: block;
  position: relative;
  overflow: hidden;
}

.skeleton-line,
.skeleton-avatar,
.skeleton-cell,
.skeleton-number,
.skeleton-rectangle {
  background-color: var(--skeleton-color, #e0e0e0);
  border-radius: 4px;
  position: relative;
  overflow: hidden;
}

.skeleton-line {
  height: 16px;
  margin-bottom: 8px;
}

.skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-cell {
  height: 20px;
  min-width: 60px;
}

.skeleton-number {
  height: 24px;
  background-color: var(--skeleton-color, #e0e0e0);
}

.skeleton-animated .skeleton-line,
.skeleton-animated .skeleton-avatar,
.skeleton-animated .skeleton-cell,
.skeleton-animated .skeleton-number,
.skeleton-animated .skeleton-rectangle {
  background: linear-gradient(90deg, var(--skeleton-color, #e0e0e0) 25%, #f0f0f0 50%, var(--skeleton-color, #e0e0e0) 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite ease-in-out;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.skeleton-card {
  padding: 16px;
}

.skeleton-table {
  width: 100%;
}

.skeleton-kpi {
  padding: 16px;
  text-align: center;
}

.skeleton-chart {
  padding: 16px;
}

.skeleton-list {
  padding: 8px;
}

.skeleton-list-item {
  padding: 8px 0;
}

/* Dark theme support */
@media (prefers-color-scheme: dark) {
  .skeleton-line,
  .skeleton-avatar,
  .skeleton-cell,
  .skeleton-number,
  .skeleton-rectangle {
    background-color: #2a2a2a;
  }
  
  .skeleton-animated .skeleton-line,
  .skeleton-animated .skeleton-avatar,
  .skeleton-animated .skeleton-cell,
  .skeleton-animated .skeleton-number,
  .skeleton-animated .skeleton-rectangle {
    background: linear-gradient(90deg, #2a2a2a 25%, #3a3a3a 50%, #2a2a2a 75%);
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .skeleton-animated .skeleton-line,
  .skeleton-animated .skeleton-avatar,
  .skeleton-animated .skeleton-cell,
  .skeleton-animated .skeleton-number,
  .skeleton-animated .skeleton-rectangle {
    animation: none;
    background: var(--skeleton-color, #e0e0e0);
  }
}
</style>
